import React, { useState, useEffect } from 'react';
import { Store, Save, Loader2, Plus, Trash2, Smartphone, Globe, CreditCard, Package, Shield, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdmin } from '@/contexts/AdminContext';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

export default function AdminStoreBillingPage() {
  const { admin } = useAdmin();
  const { isDark, colors } = useAdminTheme();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/store-billing`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => toast.error('Failed to load store config'))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => {
    setConfig(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const updatePlan = (idx, key, val) => {
    setConfig(prev => {
      const plans = [...prev.plans];
      plans[idx] = { ...plans[idx], [key]: val };
      return { ...prev, plans };
    });
    setDirty(true);
  };

  const updateFeature = (planIdx, featIdx, val) => {
    setConfig(prev => {
      const plans = [...prev.plans];
      const features = [...(plans[planIdx].features || [])];
      features[featIdx] = val;
      plans[planIdx] = { ...plans[planIdx], features };
      return { ...prev, plans };
    });
    setDirty(true);
  };

  const addFeature = (planIdx) => {
    setConfig(prev => {
      const plans = [...prev.plans];
      const features = [...(plans[planIdx].features || []), ''];
      plans[planIdx] = { ...plans[planIdx], features };
      return { ...prev, plans };
    });
    setDirty(true);
  };

  const removeFeature = (planIdx, featIdx) => {
    setConfig(prev => {
      const plans = [...prev.plans];
      const features = [...(plans[planIdx].features || [])];
      features.splice(featIdx, 1);
      plans[planIdx] = { ...plans[planIdx], features };
      return { ...prev, plans };
    });
    setDirty(true);
  };

  const addPlan = () => {
    setConfig(prev => ({
      ...prev,
      plans: [...prev.plans, {
        key: '',
        name: '',
        price_display: '',
        price_cents: 0,
        period: '/month',
        play_sku: '',
        max_devices: 1,
        features: [],
      }],
    }));
    setDirty(true);
  };

  const removePlan = (idx) => {
    setConfig(prev => {
      const plans = [...prev.plans];
      plans.splice(idx, 1);
      return { ...prev, plans };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/store-billing`, config, { withCredentials: true });
      toast.success('Store & billing config saved');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    background: isDark ? 'rgba(15,23,42,0.5)' : '#fff',
    border: `1px solid ${colors.cardBorder}`,
    color: colors.text,
  };

  const cardStyle = {
    background: isDark ? 'rgba(30,41,59,0.5)' : '#fff',
    border: `1px solid ${colors.cardBorder}`,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.textMuted }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl" data-testid="admin-store-billing-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#6366f118' }}>
              <Store className="h-5 w-5" style={{ color: '#6366f1' }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: colors.text }}>Store & Billing</h1>
              <p className="text-xs" style={{ color: colors.textMuted }}>Manage plans, pricing, Play Store SKUs, and app store config</p>
            </div>
          </div>
          {dirty && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl px-5 py-2 text-sm font-medium gap-2"
              style={{ background: colors.accent, color: '#fff' }}
              data-testid="store-billing-save"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          )}
        </div>

        {/* Subscription Plans */}
        <div className="rounded-2xl p-5 space-y-4" style={cardStyle} data-testid="plans-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#f59e0b18' }}>
                <CreditCard className="h-4 w-4" style={{ color: '#f59e0b' }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Subscription Plans</h3>
                <p className="text-[11px]" style={{ color: colors.textMuted }}>Edit plan names, pricing, features, and Play Store SKU mappings</p>
              </div>
            </div>
            <button
              onClick={addPlan}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: colors.accent, background: `${colors.accent}15` }}
              data-testid="add-plan-btn"
            >
              <Plus className="h-3.5 w-3.5" /> Add Plan
            </button>
          </div>

          {config?.plans?.map((plan, idx) => (
            <div
              key={idx}
              className="rounded-xl p-4 space-y-3"
              style={{ background: isDark ? 'rgba(15,23,42,0.4)' : '#f8fafc', border: `1px solid ${colors.cardBorder}` }}
              data-testid={`plan-card-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.accent }}>
                  Plan {idx + 1}
                </span>
                <button
                  onClick={() => removePlan(idx)}
                  className="text-red-400 hover:text-red-300 p-1"
                  data-testid={`remove-plan-${idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium mb-1 block" style={{ color: colors.textMuted }}>Plan Key (internal)</label>
                  <input
                    value={plan.key}
                    onChange={e => updatePlan(idx, 'key', e.target.value)}
                    placeholder="e.g. paid"
                    className="rounded-lg px-3 py-1.5 text-xs w-full"
                    style={inputStyle}
                    data-testid={`plan-key-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium mb-1 block" style={{ color: colors.textMuted }}>Display Name</label>
                  <input
                    value={plan.name}
                    onChange={e => updatePlan(idx, 'name', e.target.value)}
                    placeholder="e.g. Standard"
                    className="rounded-lg px-3 py-1.5 text-xs w-full"
                    style={inputStyle}
                    data-testid={`plan-name-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium mb-1 block" style={{ color: colors.textMuted }}>Display Price</label>
                  <input
                    value={plan.price_display}
                    onChange={e => updatePlan(idx, 'price_display', e.target.value)}
                    placeholder="e.g. $9.99"
                    className="rounded-lg px-3 py-1.5 text-xs w-full"
                    style={inputStyle}
                    data-testid={`plan-price-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium mb-1 block" style={{ color: colors.textMuted }}>Price (cents)</label>
                  <input
                    type="number"
                    value={plan.price_cents}
                    onChange={e => updatePlan(idx, 'price_cents', parseInt(e.target.value) || 0)}
                    className="rounded-lg px-3 py-1.5 text-xs w-full"
                    style={inputStyle}
                    data-testid={`plan-cents-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium mb-1 block" style={{ color: colors.textMuted }}>
                    <Smartphone className="h-3 w-3 inline mr-1" />
                    Google Play SKU
                  </label>
                  <input
                    value={plan.play_sku}
                    onChange={e => updatePlan(idx, 'play_sku', e.target.value)}
                    placeholder="e.g. eili_standard_monthly"
                    className="rounded-lg px-3 py-1.5 text-xs w-full"
                    style={inputStyle}
                    data-testid={`plan-sku-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium mb-1 block" style={{ color: colors.textMuted }}>Max Devices</label>
                  <input
                    type="number"
                    value={plan.max_devices}
                    onChange={e => updatePlan(idx, 'max_devices', parseInt(e.target.value) || 1)}
                    min={1}
                    className="rounded-lg px-3 py-1.5 text-xs w-full"
                    style={inputStyle}
                    data-testid={`plan-devices-${idx}`}
                  />
                </div>
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-medium" style={{ color: colors.textMuted }}>Features</label>
                  <button
                    onClick={() => addFeature(idx)}
                    className="text-[11px] font-medium flex items-center gap-1"
                    style={{ color: colors.accent }}
                    data-testid={`add-feature-${idx}`}
                  >
                    <Plus className="h-3 w-3" /> Add
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(plan.features || []).map((feat, fi) => (
                    <div key={fi} className="flex items-center gap-2">
                      <input
                        value={feat}
                        onChange={e => updateFeature(idx, fi, e.target.value)}
                        placeholder="Feature description"
                        className="rounded-lg px-3 py-1.5 text-xs flex-1"
                        style={inputStyle}
                      />
                      <button
                        onClick={() => removeFeature(idx, fi)}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Android / Google Play Config */}
        <div className="rounded-2xl p-5 space-y-4" style={cardStyle} data-testid="android-config-section">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#10b98118' }}>
              <Shield className="h-4 w-4" style={{ color: '#10b981' }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Android Configuration</h3>
              <p className="text-[11px]" style={{ color: colors.textMuted }}>Package name and signing key. Changes auto-update assetlinks.json</p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>Package Name</label>
              <input
                value={config?.android_package || ''}
                onChange={e => update('android_package', e.target.value)}
                placeholder="com.askeili.app"
                className="rounded-lg px-3 py-2 text-sm w-full font-mono"
                style={inputStyle}
                data-testid="android-package-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>SHA-256 Signing Key Fingerprint</label>
              <input
                value={config?.android_sha256 || ''}
                onChange={e => update('android_sha256', e.target.value)}
                placeholder="00:A4:49:4D:AC:48:94:52:..."
                className="rounded-lg px-3 py-2 text-sm w-full font-mono"
                style={inputStyle}
                data-testid="android-sha256-input"
              />
              <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
                Get this from Google Play Console &rarr; Setup &rarr; App signing, or from your keystore file. Saving will auto-update assetlinks.json.
              </p>
            </div>
          </div>
        </div>

        {/* App Store Links */}
        <div className="rounded-2xl p-5 space-y-4" style={cardStyle} data-testid="store-links-section">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#8b5cf618' }}>
              <ExternalLink className="h-4 w-4" style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: colors.text }}>App Store Links</h3>
              <p className="text-[11px]" style={{ color: colors.textMuted }}>URLs used for download buttons and redirect prompts</p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
                <Package className="h-3.5 w-3.5" />
                Google Play Store URL
              </label>
              <input
                value={config?.play_store_url || ''}
                onChange={e => update('play_store_url', e.target.value)}
                placeholder="https://play.google.com/store/apps/details?id=com.askeili.app"
                className="rounded-lg px-3 py-2 text-sm w-full"
                style={inputStyle}
                data-testid="play-store-url-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
                <Globe className="h-3.5 w-3.5" />
                Apple App Store URL
              </label>
              <input
                value={config?.app_store_url || ''}
                onChange={e => update('app_store_url', e.target.value)}
                placeholder="https://apps.apple.com/app/eili/id..."
                className="rounded-lg px-3 py-2 text-sm w-full"
                style={inputStyle}
                data-testid="app-store-url-input"
              />
            </div>
          </div>
        </div>

        {/* Bottom Save */}
        {dirty && (
          <div className="sticky bottom-4 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl px-6 py-2.5 text-sm font-medium gap-2 shadow-lg"
              style={{ background: colors.accent, color: '#fff' }}
              data-testid="store-billing-save-bottom"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
